import { NgClass } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { HeaderComponent } from "@components/header/header.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import { Subject, combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { AreaSelectionType, IAreaSelection, MarknadsprislistaFilter } from "~app/features/virkeskop/models/pristabell/marknadsprislistaFilter";
import { IMarknadsomrade } from "~app/features/virkeskop/models/verksamhetsomraden-models";
import { UtskriftMediatorService } from "~app/features/virkeskop/services/utskrift-mediator.service";
import { UtskriftWebinstallningarService } from "~app/features/virkeskop/services/utskrift-webinstallningar.service";

const initSelectionItems = (prislistaFilter: IAreaSelection[], marknadsomraden: IMarknadsomrade[]): SelectionItem[] =>
    marknadsomraden.map(marknadsomrade => {
        const resultatomraden: SelectionItem[] = marknadsomrade.children.map(resultatomrade => ({
            value: resultatomrade.id,
            text: resultatomrade.namn,
            selected: prislistaFilter.some(x =>
                x.typ === AreaSelectionType.resultatomrade
                && x.id === resultatomrade.id)
        }));

        const selectedResultatomradeCount = resultatomraden
            .filter(x => x.selected).length;

        const selected = prislistaFilter.some(x =>
            x.typ === AreaSelectionType.marknadsomrade
            && x.id === marknadsomrade.id);

        const partialSelected = selectedResultatomradeCount > 0
            && selectedResultatomradeCount < resultatomraden.length;

        return {
            value: marknadsomrade.id,
            text: marknadsomrade.namn,
            children: resultatomraden,
            selected,
            partialSelected
        };
    });
const mapToFilter = (items: SelectionItem[]): IAreaSelection[] => _.chain(items)
    .map((x) => [
        x,
        ...x.children
    ])
    .flatten()
    .filter(x => x.selected)
    .map(selectionItem => ({
        typ: _.isNil(selectionItem.children)
            ? AreaSelectionType.resultatomrade
            : AreaSelectionType.marknadsomrade,
        id: selectionItem.value
    }))
    .value();

@Component({
    selector: "rask-kontrakt-prislista-filter",
    templateUrl: "./kontrakt-prislista-filter.component.html",
    styleUrls: ["./kontrakt-prislista-filter.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        TreeSelectorComponent,
        CheckboxComponent,
        FormsModule,
        ButtonComponent,
        NgbPopover,
        NgClass
    ]
})
export class KontraktPrislistaFilterComponent implements OnInit, OnDestroy {
    protected filterActive = false;
    protected resultatomradeOptions: SelectionItem[] = [];
    protected visaEndastVlg = false;
    protected visaInaktuella = false;
    protected visaOavsettHsGrupper = false;

    private readonly destroy$ = new Subject<void>();
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);
    private readonly utskriftWebinstallningarService = inject(UtskriftWebinstallningarService);

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        combineLatest([
            this.utskriftWebinstallningarService.resultatomradesFilter$,
            this.utskriftMediatorService.marknadsomradeOptions$
        ]).pipe(
            takeUntil(this.destroy$)
        ).subscribe(([
            prislistaFilter,
            marknadsomraden
        ]) => {
            const options = initSelectionItems(prislistaFilter, marknadsomraden);
            this.resultatomradeOptions = options;
            this.updateFilter(mapToFilter(options));
        });
    }

    protected popoverClosed() {
        const omradenFilter = mapToFilter(this.resultatomradeOptions);
        this.utskriftWebinstallningarService.saveResultatomradesFilter(omradenFilter);
        this.updateFilter(omradenFilter);
    }

    protected reset() {
        this.visaEndastVlg = false;
        this.visaInaktuella = false;
        this.visaOavsettHsGrupper = false;

        _.chain(this.resultatomradeOptions)
            .map(x => [
                x,
                ...x.children
            ])
            .flatten()
            .value()
            .forEach(x => {
                x.partialSelected = false;
                x.selected = false;
            });
    }

    private updateFilter(omradenFilter: IAreaSelection[]) {
        const roFilter = omradenFilter.filter(x => x.typ === AreaSelectionType.resultatomrade);
        this.filterActive = roFilter.length > 0
            || this.visaEndastVlg
            || this.visaInaktuella
            || this.visaOavsettHsGrupper;

        const filter = new MarknadsprislistaFilter(
            roFilter,
            this.visaEndastVlg,
            this.visaInaktuella,
            this.visaOavsettHsGrupper
        );
        this.utskriftMediatorService.marknadsprislistaFilter$.next(filter);
    }
}
